import React, { useRef, useEffect } from 'react';
import { Modal } from '../ui';
import { InventoryItem } from '../../lib/types';

interface Props {
  open: boolean;
  onClose: () => void;
  sourceItem: InventoryItem | null;
  otherItems: InventoryItem[];
  search: string; setSearch: (s: string) => void;
  targetId: string | null; setTargetId: (v: string | null) => void;
  dropOpen: boolean; setDropOpen: (v: boolean) => void;
  busy: boolean;
  onConfirm: () => void;
}

export function InventoryMergeModal(p: Props) {
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!p.dropOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) p.setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [p.dropOpen]);

  if (!p.sourceItem) return null;

  const filteredMergeItems = p.search.trim()
    ? p.otherItems.filter(i => i.name.toLowerCase().includes(p.search.toLowerCase()))
    : p.otherItems;

  return (
    <Modal
      open={p.open}
      onClose={p.onClose}
      size="md"
      title="Merge into existing item"
      subtitle="The current item's stock will be added to the selected item, and this item will be deactivated."
      footer={
        <>
          <button className="btn btn-ghost" onClick={p.onClose} disabled={p.busy}>Cancel</button>
          <button className="btn btn-primary" onClick={p.onConfirm} disabled={p.busy || !p.targetId}>{p.busy ? 'Merging…' : 'Merge'}</button>
        </>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>Merging from:</div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
          {p.sourceItem.name} <span style={{ color: 'var(--text3)' }}>— {p.sourceItem.available} in stock</span>
        </div>
      </div>
      <div style={{ position: 'relative' }} ref={dropRef}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>Merge into:</div>
        <input
          className="input"
          value={p.search}
          onChange={e => { p.setSearch(e.target.value); p.setDropOpen(true); p.setTargetId(null); }}
          onFocus={() => p.setDropOpen(true)}
          placeholder="Search for an item to merge into…"
        />
        {p.dropOpen && (
          <div className="menu" style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4, maxHeight: 200, overflowY: 'auto', zIndex: 10 }}>
            {filteredMergeItems.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text3)' }}>No items found in this department</div>
            ) : filteredMergeItems.map(inv => (
              <button
                key={inv.id}
                className="menu-item"
                onClick={() => { p.setTargetId(inv.id); p.setSearch(inv.name); p.setDropOpen(false); }}
              >
                <span className="flex-1 text-truncate">{inv.name}</span>
                <span className="text-xs text-subtle">{inv.available} in stock</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
